import { createAsyncThunk } from "@reduxjs/toolkit";
import { AccountSummaryType, ChildInPaymentMethodMeta, LinkTFCPayloadType, PaymentMethodType, TFCBalanceResponseType, TFCPaymentRequestType, TFCPaymentResponseType, UnlinkTFCPayloadType } from "src/shared/types/src/lib/invoice.types";
import { AccountAction } from "./AccountSlice";
import { ChildrenStoreData, RootState } from "src/shared/types/src";
import { generateHMRCAuthUrl, getInvoices, getPaymentMethodsOnNursery, getTFCBalance, linkTFCAccounts, postTFCPayment, unlinkTFCAccountForChild, validateHMRCAuthSession } from "src/shared/services/src";
import { AxiosError } from "axios";
import { PAYMENT_METHODS } from "src/app/config/appConstants";

export const setAccountData = createAsyncThunk(
    "setAccountData", async ({ payload }: { payload: AccountSummaryType }, { dispatch }) => {
        try {
            dispatch(AccountAction.setAccountData(payload));
        } catch (error) {
            console.log("Error setting account data: ", error);
        }
    }
)

export const updateTransactionLoader = createAsyncThunk(
    "updateTransactionLoader", async (payload: { transactionsLoading: boolean }, { dispatch }) => {
        dispatch(AccountAction.setTransactionsLoading({ isLoading: payload.transactionsLoading }));
    }
);

export const fetchTransactions = createAsyncThunk(
    'fetchTransactions',
    async ({ child }: { child: ChildrenStoreData }, { dispatch, getState }) => {
        try {
            await dispatch(AccountAction.resetAccountOnChildChange());
            await dispatch(updateTransactionLoader({ transactionsLoading: true }));
            const response = await getInvoices({ child });
            console.log("response from transactions api : ", response);
            if (response && Object.keys(response).length > 0) {
                await dispatch(setAccountData({ payload: { balance: response?.balance, currency: response?.currency, depositAmount: response?.depositAmount } }));
                await dispatch(AccountAction.setTransactions({ transactions: response?.transactions }));
                await dispatch(AccountAction.setBillPayer({ isBillPayer: true }));
                await dispatch(AccountAction.setPaymentMethodsForChild({ availablePaymentMethods: response.availablePaymentMethods }));
                await dispatch(AccountAction.setPaymentGatewayConfig({ paymentGateway: response?.paymentGateway }))
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                const apiError = error as AxiosError;
                if (apiError && apiError.response && apiError.response.status === 403) {
                    await dispatch(AccountAction.setBillPayer({ isBillPayer: false }));
                } else {
                    throw error;
                }
            } else {
                throw error;
            }
        } finally {
            await dispatch(updateTransactionLoader({ transactionsLoading: false }));
            // console.log("state after setting everything : ", JSON.stringify((getState() as RootState).account, null, 2));
        }
    }
);

export const fetchAvailablePaymentMethods = createAsyncThunk(
    'fetchAvailablePaymentMethods',
    async (_, { dispatch, getState, fulfillWithValue }) => {
        try {
            const state = getState() as RootState;
            const nurseryId = state?.persistLogin.currentChild?.nursery?.eymanNurseryId;
            const child = state?.persistLogin.currentChild;
            const response = await getPaymentMethodsOnNursery({ child, nurseryId });
            if (response && response.hasOwnProperty('availablePaymentMethods') && response?.availablePaymentMethods?.length > 0) {
                await dispatch(AccountAction.setPaymentMethodsForNursery({ availablePaymentMethods: response.availablePaymentMethods }));
                return fulfillWithValue(response.availablePaymentMethods as PaymentMethodType[])
            } else throw new Error();
        } catch (error) {
            await dispatch(AccountAction.setPaymentMethodsForNursery({ availablePaymentMethods: [] }));
            throw error;
        }
    }
);

export const checkHMRCAuthSession = createAsyncThunk(
    'checkHMRCAuthSession',
    async (_, { rejectWithValue, fulfillWithValue }) => {
        // This function checks if the HMRC auth session is valid
        // If not, it will throw 403 error
        try {
            await validateHMRCAuthSession();
            return fulfillWithValue({});
        } catch (error) {
            return rejectWithValue({
                code: (error as any)?.response?.status,
                // message: (error as any)?.response.message
                errorObj: error
            });
        }
    }
);

// this function generates a link of HMRC for authenticating in a webview
export const generateHMRCAuthLink = createAsyncThunk(
    'generateHMRCAuthLink',
    async (_, { rejectWithValue, fulfillWithValue }) => {
        try {
            const response = await generateHMRCAuthUrl();
            if (response && (response?.authUrl || response?.hmrcPermission)) {
                return fulfillWithValue({
                    url: response?.authUrl,
                    permission: response?.hmrcPermission
                });
            } else {
                throw new Error('Could not fetch auth url');
            }
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

// function to send link TFC requests
export const sendLinkTFCAccountRequest = createAsyncThunk(
    'sendLinkTFCAccountRequest',
    async ({ child, payload }: { child: ChildrenStoreData, payload: LinkTFCPayloadType[] }, { dispatch, rejectWithValue, fulfillWithValue, getState }) => {
        try {
            const response = await linkTFCAccounts({ child, payload });
            console.log("response from linking api : ", JSON.stringify(response, null, 2));

            if (response && response.hasOwnProperty('results') && response?.results?.length > 0) {
                const allLinkedSuccessFully = response?.results?.every((r: any) => r?.status === 'success');
                if (allLinkedSuccessFully) {
                    if (response && response.hasOwnProperty('paymentMethods') && response?.paymentMethods?.availablePaymentMethods?.length > 0) {
                        const paymentMethods = response?.paymentMethods?.availablePaymentMethods;
                        await dispatch(AccountAction.setPaymentMethodsForNursery({ availablePaymentMethods: paymentMethods }));

                        // as discussed, we need to hit the transactions api again here

                        // update the transactions list to update the linking status of current child
                        await dispatch(fetchTransactions({ child }));
                    }
                }
                return fulfillWithValue(response?.results || []);
            } else throw new Error();
        } catch (error) {
            console.log("Error in TFC linking api : ", JSON.stringify(error, null, 2));
            return rejectWithValue(error);
        }
    }
);

// This function sends an unlink TFC account request
export const sendUnLinkTFCAccountRequest = createAsyncThunk(
    'sendUnLinkTFCAccountRequest',
    async ({ child, payload }: { child: ChildrenStoreData, payload: UnlinkTFCPayloadType }, { dispatch, rejectWithValue, fulfillWithValue, getState }) => {
        try {
            const response = await unlinkTFCAccountForChild({ child, payload });
            // if (response && response.hasOwnProperty('paymentMethods') && response?.paymentMethods?.availablePaymentMethods?.length > 0) {
            //     // await dispatch(AccountAction.setAvailablePaymentMethods({ availablePaymentMethods: response.availablePaymentMethods }));
            //     const paymentMethods = response?.paymentMethods?.availablePaymentMethods;
            //     console.log("payment methods: ", paymentMethods);
            //     await dispatch(AccountAction.setPaymentMethodsForNursery({ availablePaymentMethods: paymentMethods }));
            //     // update the linking status of current child 
            //     // const state = getState() as RootState;
            //     // const currentChildId = state?.persistLogin?.currentChild?.childId;
            //     // const paymentMethodsForChild = state?.account?.paymentMethodsForChild;
            //     // const updatedMethodsForChild = updateLinkingStatusOfCurrentChild(currentChildId, response.availablePaymentMethods, paymentMethodsForChild);
            //     // console.log("updated payment methods for child : ", updatedMethodsForChild);

            //     // as discussed, we need to hit the transactions api again here

            //     // update the transactions list to update the linking status of current child
            //     await dispatch(fetchTransactions({ child }));

            //     return fulfillWithValue(response?.results || []);
            // } else {
            //     throw new Error();
            // }
            return fulfillWithValue({});
        } catch (error) {
            console.log("Error in TFC unlinking api : ", JSON.stringify(error, null, 2));
            return rejectWithValue(error);
        }
    }
);

// This function fetches the TFC balance of this child account
export const fetchTFCBalance = createAsyncThunk(
    'fetchTFCBalance',
    async ({ noCache }: { noCache: boolean }, { rejectWithValue, fulfillWithValue, getState }) => {
        try {
            const state = getState() as RootState;
            const child = state?.persistLogin.currentChild;
            const response = await getTFCBalance({ child, noCache });
            console.log("response from fetch balance api : ", JSON.stringify(response, null, 2));
            if (response && Object.keys(response).length) {
                return fulfillWithValue(response as TFCBalanceResponseType);
            } else throw new Error();
        } catch (error) {
            return rejectWithValue({
                code: (error as any)?.response?.status,
                errorObj: error
            });
        }
    }
);

// This function sends a TFC payment request
export const sendTFCPaymentRequest = createAsyncThunk(
    'sendTFCPaymentRequest',
    async ({ payload }: { payload: TFCPaymentRequestType }, { rejectWithValue, fulfillWithValue, getState }) => {
        try {
            const state = getState() as RootState;
            const child = state?.persistLogin.currentChild;
            const response = await postTFCPayment({ child, payload });
            console.log("response from send tfc payment request api : ", JSON.stringify(response, null, 2));
            if (response && Object.keys(response).length) {
                return fulfillWithValue(response as TFCPaymentResponseType);
            } else throw new Error();
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

export const updateChildInPaymentMethodsForNursery = createAsyncThunk(
    'updateChildInPaymentMethodsForNursery',
    async ({ updatedChildren }: { updatedChildren: ChildInPaymentMethodMeta[] }, { dispatch, rejectWithValue, fulfillWithValue, getState }) => {
        try {
            const state = getState() as RootState;
            const paymentMethods = state?.account?.paymentMethodsForNursery;

            const updatedMethods = paymentMethods?.map((pm) => {
                if (pm?.key === PAYMENT_METHODS.TAX_FREE_CHILDCARE && updatedChildren?.length) {
                    return {
                        ...pm,
                        meta: updatedChildren
                    }
                }
                return pm;
            });

            await dispatch(AccountAction.setPaymentMethodsForNursery({ availablePaymentMethods: updatedMethods }));
        } catch (error) {
            console.log("error in updating the payment methods", error);
        }
    }
);

const updateLinkingStatusOfCurrentChild = (currentChildId: string, paymentMethodsForNursery: PaymentMethodType[], paymentMethodsForChild: PaymentMethodType[]) => {
    const tfcObjInNursery = paymentMethodsForNursery?.find((pm) => pm.key === PAYMENT_METHODS.TAX_FREE_CHILDCARE);
    const childObjInNursery = tfcObjInNursery?.meta?.find((c) => c.childId === currentChildId);
    const tfcObjInChild = paymentMethodsForChild?.find((pm) => pm.key === PAYMENT_METHODS.TAX_FREE_CHILDCARE);
    // paymentMethodsForChild?.map((pm) => {
    //     if (pm.key === PAYMENT_METHODS.TAX_FREE_CHILDCARE) {
    //         pm.meta = pm?.meta?.map((m) => {
    //             if (m.childId === currentChildId) return childObjInNursery
    //             else return m;
    //         })
    //     }
    // })
    console.log("currentChildId : ", currentChildId, JSON.stringify(childObjInNursery, null, 2));
    const updatedMeta = tfcObjInChild?.meta?.map(child => {
        return (childObjInNursery && child.childId === currentChildId) ? childObjInNursery : child;
    });

    console.log("updated meta for child : ", JSON.stringify(updatedMeta, null, 2));
    const pmForChild = paymentMethodsForChild?.map((pm) => {
        return (pm.key === PAYMENT_METHODS.TAX_FREE_CHILDCARE ? updatedMeta : pm);
    });

    console.log("payment methods for child updated: ", JSON.stringify(pmForChild, null, 2));
    return pmForChild;
}